/**
 * 标准角度配合参考实体残差校验器。
 *
 * 文件职责：
 * 1. 只处理 Angle Mate + Reference Entity 的有符号角度语义；
 * 2. 与求解器保持同一套 SOLIDWORKS 参考轴定义，避免最终残差校验退化为无符号夹角；
 * 3. 输入与输出均为纯数据和 Three.js 数学对象，不依赖 UI、场景对象或业务控制器。
 */

import * as THREE from 'three';
import {
  MateAlignment,
  type AssemblyMateDefinition,
  type MateEntityRef,
} from '../../definition';
import {
  normalizeVector,
  resolveAxisGeometry,
  resolvePlaneGeometry,
} from '../geometry/StandardMateGeometryAccess';

/** 有符号角度归一化使用的一整圈弧度。 */
const FULL_TURN_RADIANS = Math.PI * 2;

/**
 * 解析 Angle Mate 参考实体的旋转轴方向。
 *
 * @param referenceEntity 参考实体。
 * @param epsilon 角度容差。
 * @returns 归一化旋转轴；无法解析时返回 null。
 */
function resolveReferencedAngleDirection(
  referenceEntity: MateEntityRef,
  epsilon: number,
): THREE.Vector3 | null {
  const referenceAxis = resolveAxisGeometry(referenceEntity);
  if (referenceAxis) {
    return normalizeVector(referenceAxis.direction, epsilon);
  }

  const referencePlane = resolvePlaneGeometry(referenceEntity);
  if (referencePlane) {
    return normalizeVector(referencePlane.normal, epsilon);
  }

  return null;
}

/**
 * 把方向按 Mate Alignment 转成 SOLIDWORKS 角度约束的第一方向。
 *
 * @param direction 第一实体方向。
 * @param mate Mate Definition。
 * @param epsilon 角度容差。
 * @returns 对齐后的单位方向；无法归一化时返回 null。
 */
function resolveAlignedFirstDirection(
  direction: THREE.Vector3,
  mate: AssemblyMateDefinition,
  epsilon: number,
): THREE.Vector3 | null {
  const alignedDirection = mate.alignment === MateAlignment.AntiAligned
    ? direction.clone().multiplyScalar(-1)
    : direction;
  return normalizeVector(alignedDirection, epsilon);
}

/**
 * 判断参考轴是否仍能定义当前两个方向之间的有符号角度。
 *
 * 设计说明：
 * SOLIDWORKS 的角度参考实体不是视觉提示，而是用于确定角度落在参考轴哪一侧。
 * 如果最终几何已经不再与参考轴垂直，角度方向无法稳定定义，必须判定约束未满足。
 *
 * @param referenceDirection 参考旋转轴方向。
 * @param firstDirection 第一实体方向。
 * @param secondDirection 第二实体方向。
 * @param angleEpsilon 角度容差。
 * @returns 是否可用于有符号角度判断。
 */
function isReferenceDirectionCompatible(
  referenceDirection: THREE.Vector3,
  firstDirection: THREE.Vector3,
  secondDirection: THREE.Vector3,
  angleEpsilon: number,
): boolean {
  const perpendicularTolerance = Math.max(angleEpsilon * 10, 1e-5);
  return Math.abs(referenceDirection.dot(firstDirection)) <= perpendicularTolerance
    && Math.abs(referenceDirection.dot(secondDirection)) <= perpendicularTolerance;
}

/**
 * 计算绕参考轴从第一方向到第二方向的有符号角度。
 *
 * @param firstDirection 第一实体方向。
 * @param secondDirection 第二实体方向。
 * @param referenceDirection 参考旋转轴方向。
 * @returns 范围为 [-PI, PI] 的有符号角度。
 */
function calculateSignedAngleAroundReference(
  firstDirection: THREE.Vector3,
  secondDirection: THREE.Vector3,
  referenceDirection: THREE.Vector3,
): number {
  const signedNumerator = referenceDirection.dot(firstDirection.clone().cross(secondDirection));
  const signedDenominator = THREE.MathUtils.clamp(firstDirection.dot(secondDirection), -1, 1);
  return Math.atan2(signedNumerator, signedDenominator);
}

/**
 * 归一化两个有符号角之间的差值。
 *
 * @param angleDelta 原始角度差。
 * @returns 范围为 [-PI, PI] 的角度差。
 */
function normalizeSignedAngleDelta(angleDelta: number): number {
  const normalized = ((angleDelta + Math.PI) % FULL_TURN_RADIANS + FULL_TURN_RADIANS) % FULL_TURN_RADIANS;
  return normalized - Math.PI;
}

/**
 * 校验带参考实体的 Angle Mate 是否仍满足 SOLIDWORKS 有符号角约束。
 *
 * @param mate Angle Mate Definition。
 * @param firstDirection 第一实体当前方向。
 * @param secondDirection 第二实体当前方向。
 * @param angleEpsilon 角度容差。
 * @returns 是否满足；参考实体缺失或无法解析时返回 null。
 */
export function validateReferencedAngleMate(
  mate: AssemblyMateDefinition,
  firstDirection: THREE.Vector3,
  secondDirection: THREE.Vector3,
  angleEpsilon: number,
): boolean | null {
  if (!mate.referenceEntity) {
    return null;
  }

  const referenceDirection = resolveReferencedAngleDirection(mate.referenceEntity, angleEpsilon);
  const alignedFirstDirection = resolveAlignedFirstDirection(firstDirection, mate, angleEpsilon);
  const normalizedSecondDirection = normalizeVector(secondDirection, angleEpsilon);
  if (!referenceDirection || !alignedFirstDirection || !normalizedSecondDirection) {
    return null;
  }

  if (!isReferenceDirectionCompatible(
    referenceDirection,
    alignedFirstDirection,
    normalizedSecondDirection,
    angleEpsilon,
  )) {
    return false;
  }

  const currentSignedAngle = calculateSignedAngleAroundReference(
    alignedFirstDirection,
    normalizedSecondDirection,
    referenceDirection,
  );
  const desiredSignedAngle = (mate.dimension?.flipped ?? false)
    ? -(mate.dimension?.value ?? 0)
    : (mate.dimension?.value ?? 0);

  return Math.abs(normalizeSignedAngleDelta(currentSignedAngle - desiredSignedAngle)) <= angleEpsilon;
}
